import React, { useEffect, useState } from 'react';
import {
  Flex,
  Stack,
  StackDivider,
  Text,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { pick } from 'lodash-es';
import {
  PLATFORM,
  NOTIFICATION_TYPE,
  NOTIFICATION_TYPE_TOPIC_MAP,
} from '../../../../../constants/benchat';
import benchatService from '../../../../../service/benchatService';
import { Bell as BellIcon } from '../../../../context/Chakra/Icons';
import { useAppContext } from '../../../context/appContext';
import { SECTION_STATUS } from '../../../constants';
import ErrorPanel from '../components/ErrorPanel';
import EmailSubscription from '../components/EmailSubscription';
import GeneralSubscription from '../components/GeneralSubscription';
import FacebookSubscription from '../components/FacebookSubscription';
import LineSubscription from '../components/LineSubscription';
import Loading from '../components/Loading';
import { useProfileContext } from '../context/profileContext';

const SubscriptionSection = () => {
  const { t } = useTranslation();
  const {
    currentUser,
    hasConnectedToWhatsapp,
    slFeatureService,
    facebookSubscriptionConfig,
    lineSubscriptionConfig,
  } = useAppContext();
  const { userData } = useProfileContext();
  const showOrderSubscriptionSection =
    facebookSubscriptionConfig.enabled.order ||
    lineSubscriptionConfig.enabled.order;
  const shouldShowWhatsApp =
    slFeatureService.hasFeature('whatsapp_accept_marketing') &&
    hasConnectedToWhatsapp;
  const shouldCheckMemberReward =
    slFeatureService.hasFeature('member_info_reward_subscription') &&
    !userData?.info_reward_claimed;
  const theme = useTheme();
  const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const [subscribedMap, setSubscribedMap] = useState(null);
  const [status, setStatus] = useState(SECTION_STATUS.INITIAL);
  const getSubscriptions = async () => {
    try {
      setStatus(SECTION_STATUS.LOADING);
      const response = await benchatService.getSubscriptions(currentUser._id, [
        ...NOTIFICATION_TYPE_TOPIC_MAP[NOTIFICATION_TYPE.MARKETING],
        ...NOTIFICATION_TYPE_TOPIC_MAP[NOTIFICATION_TYPE.ORDER],
      ]);
      const { subscriptions } = response.data;
      setSubscribedMap({
        [NOTIFICATION_TYPE.MARKETING]: Object.values(
          pick(PLATFORM, ['SMS', 'LINE_MES_API', 'FACEBOOK', 'WHATSAPP']),
        ).reduce((accumulate, platform) => {
          accumulate[platform] = benchatService.filterByPlatformAndTopics({
            subscriptions,
            platform,
            topics: NOTIFICATION_TYPE_TOPIC_MAP[NOTIFICATION_TYPE.MARKETING],
          });
          return accumulate;
        }, {}),
        [NOTIFICATION_TYPE.ORDER]: Object.values(
          pick(PLATFORM, ['LINE_MES_API', 'FACEBOOK']),
        ).reduce((accumulate, platform) => {
          accumulate[platform] = benchatService.filterByPlatformAndTopics({
            subscriptions,
            platform,
            topics: NOTIFICATION_TYPE_TOPIC_MAP[NOTIFICATION_TYPE.ORDER],
          });
          return accumulate;
        }, {}),
      });
      setStatus(SECTION_STATUS.SUCCESS);
    } catch (error) {
      console.error(error);
      setStatus(SECTION_STATUS.FAILURE);
    }
  };

  useEffect(() => {
    getSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      direction="column"
      p="20px"
      gap="20px"
      border="1px solid #dddddd"
      className="member-center-profile__form-section"
    >
      <Flex
        gap="10px"
        alignItems="center"
        className="member-center-profile-section_title"
      >
        <BellIcon
          fontSize="20px"
          color="#dddddd"
          className="icon-user member-center-profile__section-title-icon-icon"
        />
        <Text
          variant="sectionTitle"
          className="member-center-profile__section-title-text"
        >
          {t('member_profile.subscription')}
        </Text>
      </Flex>
      {status === SECTION_STATUS.LOADING && <Loading />}
      {status === SECTION_STATUS.FAILURE && (
        <ErrorPanel onReload={getSubscriptions} hideErrorImg />
      )}
      {status === SECTION_STATUS.SUCCESS && (
        <Stack
          direction={isDesktop ? 'row' : 'column'}
          spacing="20px"
          divider={<StackDivider className="member-subscription__divider" />}
        >
          <Flex w="100%" direction="column" gap="15px">
            <Text
              variant="sectionSubTitle"
              className="member-center-profile__section-sub-title"
              textAlign="left"
            >
              {t('member_profile.notification.promotion')}
            </Text>
            <EmailSubscription
              shouldCheckMemberReward={shouldCheckMemberReward}
            />
            <GeneralSubscription
              notificationType={NOTIFICATION_TYPE.MARKETING}
              platform={PLATFORM.SMS}
              subscriptions={
                subscribedMap[NOTIFICATION_TYPE.MARKETING][PLATFORM.SMS]
              }
              title="SMS"
              e2eId="member-center-profile-marketing-subscription_button--sms"
              shouldCheckMemberReward={shouldCheckMemberReward}
            />
            {shouldShowWhatsApp && (
              <GeneralSubscription
                notificationType={NOTIFICATION_TYPE.MARKETING}
                platform={PLATFORM.WHATSAPP}
                subscriptions={
                  subscribedMap[NOTIFICATION_TYPE.MARKETING][PLATFORM.WHATSAPP]
                }
                title="WhatsApp"
                e2eId="member-center-profile-marketing-subscription_button--whatsapp"
                shouldCheckMemberReward={shouldCheckMemberReward}
              />
            )}
            {lineSubscriptionConfig.enabled.marketing && (
              <LineSubscription
                notificationType={NOTIFICATION_TYPE.MARKETING}
                subscriptions={
                  subscribedMap[NOTIFICATION_TYPE.MARKETING][
                    PLATFORM.LINE_MES_API
                  ]
                }
                e2eId="member-center-profile-marketing-subscription_button--line"
                shouldCheckMemberReward={shouldCheckMemberReward}
              />
            )}
            {facebookSubscriptionConfig.enabled.marketing && (
              <FacebookSubscription
                notificationType={NOTIFICATION_TYPE.MARKETING}
                subscriptions={
                  subscribedMap[NOTIFICATION_TYPE.MARKETING][PLATFORM.FACEBOOK]
                }
                e2eId="member-center-profile-marketing-subscription_button--facebook"
                shouldCheckMemberReward={shouldCheckMemberReward}
              />
            )}
          </Flex>
          {showOrderSubscriptionSection ? (
            <Flex w="100%" direction="column" gap="15px">
              <Text
                variant="sectionSubTitle"
                className="member-center-profile__section-sub-title"
                textAlign="left"
              >
                {t('member_profile.notification.order')}
              </Text>
              {lineSubscriptionConfig.enabled.order && (
                <LineSubscription
                  notificationType={NOTIFICATION_TYPE.ORDER}
                  subscriptions={
                    subscribedMap[NOTIFICATION_TYPE.ORDER][
                      PLATFORM.LINE_MES_API
                    ]
                  }
                  e2eId="member-center-profile-order-subscription_button--line"
                />
              )}
              {facebookSubscriptionConfig.enabled.order && (
                <FacebookSubscription
                  notificationType={NOTIFICATION_TYPE.ORDER}
                  subscriptions={
                    subscribedMap[NOTIFICATION_TYPE.ORDER][PLATFORM.FACEBOOK]
                  }
                  e2eId="member-center-profile-order-subscription_button--facebook"
                />
              )}
            </Flex>
          ) : null}
        </Stack>
      )}
    </Flex>
  );
};

export default SubscriptionSection;
